/**
Author - Brijesh Pandey
Git - https://bitbucket.org/__brijesh/
**/

import React, {useEffect, useState, useCallback, useRef} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {
  getCvlStatus,
  getDigilockerData,
  getSpecimenSign,
  getEsignRequest,
  getDdpiRequest,
  getSelfieRequest,
  checkPanAadharSeeded,
  uploadPanCard,
} from 'api';

import {handleApiError} from 'helpers';

import InitiateApiCalls from './InitiateApiCalls';
import useMSClarity from 'hooks/useMSClarity';
import OfflineSignComponent from 'components/OfflineSignComponent';
import {logEsignStartedEvent} from 'utils/kycFirebaseStepEvents';

const propTypes = {
  kycData: PropTypes.object.isRequired,
  updateStatus: PropTypes.func.isRequired,
  handleErrorState: PropTypes.func,
};

const InitiateApiCallsContainer = ({kycData, updateStatus, handleErrorState}) => {
  const [isError, setIsError] = useState(false);
  const [digioDetails, setDigioDetails] = useState('');

  const [signingType, setSigningType] = useState();

  const {sendClarityEvent, setClarityCustomTag} = useMSClarity();

  const handleApiCall = useCallback(
    async (correspondingCall) => {
      try {
        const response = await correspondingCall();
        const result = response.data;
        const digioDetailsPresent = !!result.digioDetails;

        if (kycData.stage === 'E_SIGN' && result?.signingType) {
          setSigningType(result.signingType);
        }

        if (digioDetailsPresent) {
          setDigioDetails(result.digioDetails);
          if (kycData.stage === 'E_SIGN') {
            logEsignStartedEvent(kycData.flow, kycData);
          }
        } else {
          updateStatus();
        }
      } catch (error) {
        handleApiError(error);
        setIsError(true);
      }
    },
    [kycData.stage, setSigningType, setDigioDetails, updateStatus, setIsError]
  );

  const esignCalledRef = useRef(false);

  useEffect(() => {
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = 'Reload is nor recommended unless if you find any issue ?';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  useEffect(() => {
    setClarityCustomTag('kyc_id', kycData?.id);
    sendClarityEvent(`STEP_INIT_API_CALL_${kycData.stage}_${kycData.id}`);

    switch (kycData.stage) {
      case 'CVL':
        handleApiCall(getCvlStatus);
        break;

      case 'DIGILOCKER':
        handleApiCall(getDigilockerData);
        break;

      case 'SPECIMEN_SIGN':
        handleApiCall(getSpecimenSign);
        break;

      case 'SELFIE':
        handleApiCall(getSelfieRequest);
        break;

      case 'E_SIGN':
        if (!esignCalledRef.current) {
          esignCalledRef.current = true;
          const timestamp = new Date().getTime();
          handleApiCall(() => getEsignRequest(timestamp));
        }
        break;

      case 'DDPI':
        handleApiCall(getDdpiRequest);
        break;

      case 'PAN_ANALYSIS':
        handleApiCall(checkPanAadharSeeded);
        break;

      case 'UPLOAD_PAN':
        handleApiCall(uploadPanCard);
        break;

      default:
        break;
    }
  }, []);

  if (kycData.stage === 'E_SIGN' && signingType === 'OFFLINE_SIGN')
    return (
      <Container>
        <OfflineSignComponent kycData={kycData} />
      </Container>
    );

  return (
    <>
      <InitiateApiCalls
        digioDetails={digioDetails}
        isError={isError}
        setDigioDetails={setDigioDetails}
        kycData={kycData}
        updateStatus={updateStatus}
        handleErrorState={handleErrorState}
      />
    </>
  );
};

const Container = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: ${(props) => props.theme.colors.background.primary};
  font-family: ${(props) => props.theme.typography.fontFamily.primary};

  @media (min-width: ${(props) => props.theme.breakpoints.md}) {
    max-width: 800px;
    margin: 0 auto;
  }
`;

InitiateApiCallsContainer.propTypes = propTypes;

export default InitiateApiCallsContainer;
